import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  X, GripVertical, Loader2, ArrowRight, ChevronLeft, ChevronRight,
  LayoutGrid, User, PenLine, CreditCard, Check,
} from 'lucide-react';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { API_URL } from '../config/api';
import { getOrgId } from '../utils/apiV2';

// Kiosk Direct flow builder — a left-to-right "pegboard" of modules the walk-in
// guest moves through. Drag Services / Guest details / Waiver anywhere; Checkout
// is locked and must always come after Services + Guest details.
const MODULES = {
  services: { icon: LayoutGrid, title: 'Services', desc: 'Pick a departure', step: 'pick a departure' },
  contact: { icon: User, title: 'Guest details', desc: 'Name + contact', step: 'enter their details' },
  waiver: { icon: PenLine, title: 'Waiver', desc: 'Signatures', step: 'sign the waiver' },
  checkout: { icon: CreditCard, title: 'Checkout', desc: 'Pay via Stripe', step: 'pay' },
};
const ALL = ['services', 'contact', 'waiver', 'checkout'];
const DEFAULT_ORDER = ['contact', 'services', 'checkout', 'waiver'];

const deriveOrder = (df) => {
  const pair = df.services_first === true ? ['services', 'contact'] : ['contact', 'services'];
  return df.waiver_position === 'before_payment' ? [...pair, 'waiver', 'checkout'] : [...pair, 'checkout', 'waiver'];
};

const isValidOrder = (ord) => {
  const c = ord.indexOf('checkout');
  const w = ord.indexOf('waiver');
  if (c < ord.indexOf('services') || c < ord.indexOf('contact')) return false;
  return w === c - 1 || w > c; // signatures need a booking — the waiver hugs checkout
};

const reordered = (ord, id, toIdx) => {
  const next = ord.filter((m) => m !== id);
  next.splice(toIdx, 0, id);
  return next;
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
});

export const KioskFlowBuilder = ({ onClose }) => {
  const orgId = getOrgId();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flow, setFlow] = useState(null);
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [requirePhone, setRequirePhone] = useState(true);
  const [requireEmail, setRequireEmail] = useState(true);
  const [waiverOtp, setWaiverOtp] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const waiverFirst = order.indexOf('waiver') < order.indexOf('checkout');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/flows/org/${orgId}/kiosk-direct`, { headers: authHeaders() });
        if (!res.ok) throw new Error('load failed');
        const data = await res.json();
        setFlow(data);
        const df = data?.step_config?.direct_flow || {};
        const ord = Array.isArray(df.order) && df.order.length === 4 && ALL.every((m) => df.order.includes(m))
          ? df.order : deriveOrder(df);
        setOrder(isValidOrder(ord) ? ord : DEFAULT_ORDER);
        setRequirePhone(df.require_phone !== false);
        setRequireEmail(df.require_email !== false);
        setWaiverOtp(df.waiver_otp !== false);
      } catch (e) {
        toast.error('Could not load your Kiosk Direct flow');
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId]);

  const move = (id, toIdx) => {
    const cur = order.indexOf(id);
    if (cur === -1 || toIdx < 0 || toIdx > order.length - 1 || toIdx === cur) return;
    const next = reordered(order, id, toIdx);
    if (!isValidOrder(next)) {
      const c = next.indexOf('checkout');
      if (c < next.indexOf('services') || c < next.indexOf('contact')) {
        toast.error("Checkout can't run before Services and Guest details");
      } else {
        toast.error('Signatures need a booking first — the waiver sits right before Checkout or after it');
      }
      return;
    }
    setOrder(next);
  };

  const canDrop = (id, toIdx) => isValidOrder(reordered(order, id, toIdx));

  const toggleContact = (field, value) => {
    if (field === 'phone') {
      if (!value && !requireEmail) return toast.error('At least one contact point (phone or email) must stay required');
      setRequirePhone(value);
    } else {
      if (!value && !requirePhone) return toast.error('At least one contact point (phone or email) must stay required');
      setRequireEmail(value);
    }
  };

  const save = async () => {
    if (!flow) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/flows/org/${orgId}/${flow.id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({
          step_config: {
            ...(flow.step_config || {}),
            mode: 'direct',
            direct_flow: {
              ...(flow.step_config?.direct_flow || {}),
              order,
              waiver_position: waiverFirst ? 'before_payment' : 'after_payment',
              services_first: order.indexOf('services') < order.indexOf('contact'),
              require_phone: requirePhone,
              require_email: requireEmail,
              waiver_otp: waiverOtp,
            },
          },
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      toast.success('Kiosk Direct flow saved — applies the next time the kiosk loads');
      onClose();
    } catch (e) {
      toast.error(e.message || 'Could not save the flow');
    } finally {
      setSaving(false);
    }
  };

  const summary = order.map((id) => (id === 'waiver' && !waiverFirst ? 'sign the waiver (from their ticket)' : MODULES[id].step));

  // Every module is draggable — validation keeps orders honest (checkout after
  // services+contact; waiver hugging checkout). Checkout can swap 3↔4 with waiver.
  const renderCard = (id, index) => {
    const m = MODULES[id];
    const dragging = !!dragId;
    const isSource = dragId === id;
    const validTarget = dragging && !isSource && canDrop(dragId, index);
    const isOver = overIdx === index && validTarget;
    return (
      <div
        key={id}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          try { e.dataTransfer.setData('text/plain', id); } catch (err) { /* IE */ }
          // Defer the state update — a sync re-render inside dragstart aborts the drag
          requestAnimationFrame(() => setDragId(id));
        }}
        onDragEnd={() => { setDragId(null); setOverIdx(null); }}
        onDragOver={(e) => { if (dragging && validTarget) { e.preventDefault(); setOverIdx(index); } }}
        onDragLeave={() => setOverIdx((v) => (v === index ? null : v))}
        onDrop={(e) => { e.preventDefault(); if (dragId) move(dragId, index); setDragId(null); setOverIdx(null); }}
        data-testid={`flow-module-${id}`}
        className={`relative flex flex-col items-center text-center rounded-xl border bg-white px-3 pt-4 pb-2 w-[138px] flex-shrink-0 select-none transition-[box-shadow,border-color,opacity,background-color]
          cursor-grab active:cursor-grabbing border-blue-200 shadow-sm hover:shadow-md
          ${isSource ? 'border-dashed border-blue-500 opacity-70' : ''}
          ${validTarget && !isOver ? 'border-blue-400 border-dashed bg-blue-50/40' : ''}
          ${isOver ? 'ring-2 ring-blue-500 border-blue-500 bg-blue-50' : ''}
          ${dragging && !isSource && !validTarget ? 'opacity-30 grayscale' : ''}`}
      >
        <span className="absolute top-1.5 left-1/2 -translate-x-1/2 h-1.5 w-1.5 rounded-full bg-gray-200" aria-hidden />
        <span className="absolute -top-2 -left-2 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold bg-blue-600 text-white">{index + 1}</span>
        <span className="flex h-9 w-9 items-center justify-center rounded-xl mb-1.5 pointer-events-none bg-blue-600 text-white">
          <m.icon style={{ width: 17, height: 17 }} />
        </span>
        <p className="text-[13px] font-bold text-gray-900 leading-tight pointer-events-none">{m.title}</p>
        <p className="text-[10.5px] text-gray-500 mt-0.5 pointer-events-none">{m.desc}</p>
        <div className="flex items-center gap-1 mt-1.5">
          <button
            onClick={() => move(id, index - 1)}
            disabled={index === 0}
            data-testid={`flow-module-left-${id}`}
            className="h-5 w-5 rounded-md border border-gray-200 flex items-center justify-center text-gray-400 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-25"
            title="Move earlier"
          >
            <ChevronLeft className="w-3 h-3" />
          </button>
          <GripVertical className="w-3 h-3 text-blue-300" />
          <button
            onClick={() => move(id, index + 1)}
            disabled={index === order.length - 1}
            data-testid={`flow-module-right-${id}`}
            className="h-5 w-5 rounded-md border border-gray-200 flex items-center justify-center text-gray-400 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-25"
            title="Move later"
          >
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="kiosk-flow-builder">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 pb-3 flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Your Kiosk Direct flow</h3>
            <p className="text-xs text-gray-500 mt-0.5">Grab any module and drag it where you want it — valid spots light up. Checkout always comes after Services + Guest details, and the waiver sticks with checkout (before or after it).</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100 flex-shrink-0" data-testid="flow-builder-close">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="px-5 pb-2 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400 gap-2 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading your flow…
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {/* Pegboard */}
              <div
                data-testid="flow-pegboard"
                className="rounded-2xl border border-gray-200 p-4 overflow-x-auto"
                style={{
                  background: '#f5f8fc',
                  backgroundImage: 'radial-gradient(circle, #d5deec 1.2px, transparent 1.2px)',
                  backgroundSize: '16px 16px',
                }}
              >
                <div className="flex items-center gap-1.5 min-w-max py-1 px-0.5">
                  {order.map((id, i) => (
                    <React.Fragment key={id}>
                      {renderCard(id, i)}
                      {i < order.length - 1 && (
                        <ArrowRight className="w-4 h-4 text-blue-400 flex-shrink-0" aria-hidden />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <p className="text-xs text-gray-600" data-testid="flow-summary">
                <span className="font-semibold text-gray-800">Guests will:</span> {summary.join(' → ')}.
              </p>

              {/* Guest details config */}
              <div className="rounded-xl border border-gray-200 p-4 space-y-2.5" data-testid="flow-contact-config">
                <p className="text-sm font-bold text-gray-900 flex items-center gap-2"><User className="w-3.5 h-3.5 text-blue-600" /> Guest details</p>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-gray-700">Phone required</span>
                  <Switch checked={requirePhone} onCheckedChange={(v) => toggleContact('phone', v)} data-testid="flow-require-phone" />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-gray-700">Email required</span>
                  <Switch checked={requireEmail} onCheckedChange={(v) => toggleContact('email', v)} data-testid="flow-require-email" />
                </div>
                <p className="text-[10.5px] text-gray-400">Name is always required, and at least one contact point stays required — that&apos;s how tickets (email) and WhatsApp/SMS (phone) reach the guest. If email is skipped, Stripe still collects one at checkout.</p>
              </div>

              {/* Waiver config */}
              <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-4 space-y-2.5" data-testid="flow-waiver-config">
                <p className="text-sm font-bold text-gray-900 flex items-center gap-2"><PenLine className="w-3.5 h-3.5 text-blue-600" /> Waiver</p>
                {waiverFirst ? (
                  <>
                    <p className="text-[10.5px] text-gray-500">Guests sign right after booking, before Stripe. One signature unlocks checkout — extra guests can keep signing, but checkout is never blocked.</p>
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <span className="text-xs font-medium text-gray-700">Identity verification (OTP)</span>
                      <Switch checked={waiverOtp} onCheckedChange={setWaiverOtp} data-testid="flow-waiver-otp" />
                    </div>
                    <p className="text-[10.5px] text-gray-500">{waiverOtp
                      ? 'Signers confirm a 6-digit code sent to their email/phone — proves their device signed it (ID fallback available). Your standard waiver process.'
                      : 'Signers skip the code — fastest at the counter. Signatures are recorded as kiosk walk-ins instead of device-verified.'}</p>
                  </>
                ) : (
                  <p className="text-[10.5px] text-gray-500">Signatures are collected right after payment — guests get a Sign Waiver button on their ticket. Drag the waiver before Checkout to collect signatures first.</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="p-5 pt-3 border-t border-gray-100 flex items-center justify-between gap-3 flex-shrink-0">
          <p className="text-[11px] text-gray-400">Applies the next time Kiosk Direct loads.</p>
          <Button onClick={save} disabled={saving || loading} className="bg-blue-600 hover:bg-blue-700 text-white gap-2 rounded-full px-5 flex-shrink-0" data-testid="flow-builder-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save flow
          </Button>
        </div>
      </div>
    </div>
  );
};

export default KioskFlowBuilder;
